import React, { Suspense } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import lazyWithRetry from "../utils/lazyWithRetry";
import ErrorBoundary from "../components/common/ErrorBoundary";
import { IS_CHAT_ENABLED } from "../config/features";

// Load HomePage directly for instant landing page paint
import HomePage from "../pages/HomePage";

// Lazy load all other pages on demand with automatic deployment retry wrapper
const HomeDynamicPage = lazyWithRetry(() => import("../pages/HomeDynamicPage"));
const SearchProfile = lazyWithRetry(() => import("../pages/SearchProfile"));
const Registration = lazyWithRetry(() => import("../pages/Registration"));
const RegistrationSuccess = lazyWithRetry(() => import("../pages/RegistrationSuccess"));
const SendOtp = lazyWithRetry(() => import("../pages/SendOtp"));
const BasicDetails = lazyWithRetry(() => import("../pages/BasicDetails"));
const ContactInformation = lazyWithRetry(() => import("../pages/ContactInformation"));
const AadhaarVerification = lazyWithRetry(() => import("../pages/AadhaarVerification"));
const AadhaarOtp = lazyWithRetry(() => import("../pages/AadhaarOtp"));
const ReligionPage = lazyWithRetry(() => import("../pages/ReligionPage"));
const LocationDetail = lazyWithRetry(() => import("../pages/LocationDetail"));
const FamilyDetail = lazyWithRetry(() => import("../pages/FamilyDetail"));
const EducationDetail = lazyWithRetry(() => import("../pages/EducationDetail"));
const ProfessionDetail = lazyWithRetry(() => import("../pages/ProfessionDetail"));
const ImageUpload = lazyWithRetry(() => import("../pages/ImageUpload"));
const PartnerQualities = lazyWithRetry(() => import("../pages/PartnerQualities"));
const PartnerBasicDetail = lazyWithRetry(() => import("../pages/PartnerBasicDetail"));
const HobbiesEdit = lazyWithRetry(() => import("../pages/HobbiesEdit"));
const HoroscopeEdit = lazyWithRetry(() => import("../pages/HoroscopeEdit"));
const ContactUs = lazyWithRetry(() => import("../pages/ContactUs"));
const Dashboard = lazyWithRetry(() => import("../pages/Dashboard"));
const MyProfile = lazyWithRetry(() => import("../pages/MyProfile"));
const AboutUs = lazyWithRetry(() => import("../pages/AboutUs"));
const BlogList = lazyWithRetry(() => import("../pages/BlogList"));
const BlogDetails = lazyWithRetry(() => import("../pages/BlogDetails"));
const BasicSearch = lazyWithRetry(() => import("../pages/BasicSearch"));
const ProfileDetail = lazyWithRetry(() => import("../pages/ProfileDetail"));
const Inbox = lazyWithRetry(() => import("../pages/Inbox"));
const InboxSent = lazyWithRetry(() => import("../pages/InboxSent"));
const InboxReceived = lazyWithRetry(() => import("../pages/InboxReceived"));
const InboxAccepted = lazyWithRetry(() => import("../pages/InboxAccepted"));
const Package = lazyWithRetry(() => import("../pages/Package"));
const ShortList = lazyWithRetry(() => import("../pages/ShortList"));
const Settings = lazyWithRetry(() => import("../pages/Settings"));
const Notification = lazyWithRetry(() => import("../pages/Notification"));
const RecentVisitors = lazyWithRetry(() => import("../pages/RecentVisitors"));
const PhotoRequestList = lazyWithRetry(() => import("../pages/PhotoRequestList"));
const DobRequestList = lazyWithRetry(() => import("../pages/DobRequestList"));
const ChatPage = lazyWithRetry(() => import("../pages/ChatPage"));
const PrivacyPolicy = lazyWithRetry(() => import("../pages/PrivacyPolicy"));
const TermsAndCondition = lazyWithRetry(() => import("../pages/TermsAndCondition"));
const Disclaimer = lazyWithRetry(() => import("../pages/Disclaimer"));
const DownloadApp = lazyWithRetry(() => import("../pages/DownloadApp"));
const NotFound = lazyWithRetry(() => import("../pages/NotFound"));

const RouteLoader = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{
      width: "36px",
      height: "36px",
      border: "3px solid #f0f0f0",
      borderTop: "3px solid #ff3366",
      borderRadius: "50%",
      animation: "spin 0.7s linear infinite"
    }} />
  </div>
);

export default function Router() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/state/:slug" element={<HomeDynamicPage />} />
            <Route path="/city/:stateSlug/:citySlug" element={<HomeDynamicPage />} />
            <Route path="/city/:slug" element={<HomeDynamicPage />} />
            <Route path="/religion/:slug" element={<HomeDynamicPage />} />
            <Route path="/caste/:slug" element={<HomeDynamicPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndCondition />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/download" element={<DownloadApp />} />
            <Route path="/app-download" element={<DownloadApp />} />

            <Route path="/search-profile" element={<SearchProfile />} />
            <Route path="/signup" element={<Navigate to="/registration" replace />} />
            <Route path="/signup/:userId" element={<Navigate to="/registration" replace />} />

            <Route path="/registration" element={<Registration />} />
            <Route path="/registration/:userId" element={<Registration />} />
            <Route path="/send-otp" element={<SendOtp />} />
            <Route path="/send-otp/:userId" element={<SendOtp />} />
            <Route path="/basic-details" element={<BasicDetails />} />
            <Route path="/basic-details-edit" element={<BasicDetails />} />
            <Route path="/contact-information" element={<ContactInformation />} />
            <Route path="/contact-information-edit" element={<ContactInformation />} />
            <Route path="/aadhaar-verification" element={<AadhaarVerification />} />
            <Route path="/aadhaar-verification-edit" element={<AadhaarVerification />} />
            <Route path="/aadhaar-otp" element={<AadhaarOtp />} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
            <Route path="/religion" element={<ReligionPage />} />
            <Route path="/religion-edit" element={<ReligionPage />} />
            <Route path="/location-detail" element={<LocationDetail />} />
            <Route path="/location-detail-edit" element={<LocationDetail />} />
            <Route path="/family-detail" element={<FamilyDetail />} />
            <Route path="/family-detail-edit" element={<FamilyDetail />} />
            <Route path="/education-detail" element={<EducationDetail />} />
            <Route path="/education-detail-edit" element={<EducationDetail />} />
            <Route path="/profession-detail" element={<ProfessionDetail />} />
            <Route path="/profession-detail-edit" element={<ProfessionDetail />} />
            <Route path="/profession-details-edit" element={<ProfessionDetail />} />
            <Route path="/profile-photo" element={<ImageUpload />} />

            <Route path="/profile-photo-edit" element={<ImageUpload />} />
            <Route path="/partner-qualities" element={<PartnerQualities />} />
            <Route path="/partner-basic-detail" element={<PartnerBasicDetail />} />
            <Route path="/partner-basic-detail-edit" element={<PartnerBasicDetail />} />
            <Route path="/hobbies-edit" element={<HobbiesEdit />} />
            <Route path="/hobbies" element={<HobbiesEdit />} />
            <Route path="/horoscope-details-edit" element={<HoroscopeEdit />} />
            <Route path="/horoscope-edit" element={<HoroscopeEdit />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/my-profile" element={<MyProfile />} />
            <Route path="/my-profile/partner" element={<MyProfile />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/blogs" element={<BlogList />} />
            <Route path="/blogs/:slug" element={<BlogDetails />} />
            <Route path="/basic-search" element={<BasicSearch />} />
            <Route path="/advance-search" element={<BasicSearch />} />
            <Route path="/profile-details/:profileId" element={<ProfileDetail />} />
            <Route path="/inbox/received" element={<InboxReceived />} />
            <Route path="/inbox/accepted" element={<InboxAccepted />} />
            <Route path="/inbox/decline" element={<Inbox />} />
            <Route path="/inbox/sent" element={<InboxSent />} />
            <Route path="/my-matches" element={<SearchProfile />} />
            <Route path="/today-matches" element={<SearchProfile />} />
            <Route path="/near-me" element={<SearchProfile />} />
            <Route path="/search-profile/:caste" element={<SearchProfile />} />
            <Route path="/membership-plan" element={<Package />} />
            <Route path="/shortlist" element={<ShortList />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/notification" element={<Notification />} />
            <Route path="/recent-visitors" element={<RecentVisitors />} />
            <Route path="/photo-request" element={<PhotoRequestList />} />
            <Route path="/dob-request" element={<DobRequestList />} />
            <Route
              path="/chat-box/:profileId"
              element={IS_CHAT_ENABLED ? <ChatPage /> : <Navigate to="/dashboard" replace />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}


